import React, { useState, useRef } from 'react';
import { Crown, Star, TrendingUp, Info } from 'lucide-react';
import type { Team } from '@bracketblitz/shared-types';

interface TeamSlotProps {
  team?: Team;
  seed?: number;
  isSelected?: boolean;
  isEliminated?: boolean;
  isCorrect?: boolean;
  isIncorrect?: boolean;
  isLocked?: boolean;
  disabled?: boolean;
  showStatsButton?: boolean;
  confidence?: number;
  onClick?: () => void;
  size?: 'sm' | 'md' | 'lg';
  showSeed?: boolean;
  showRecord?: boolean;
  className?: string;
}

const TeamSlotComponent: React.FC<TeamSlotProps> = ({
  team,
  seed,
  isSelected = false,
  isEliminated = false,
  isCorrect = false,
  isIncorrect = false,
  isLocked = false,
  disabled = false,
  showStatsButton = true,
  confidence,
  onClick,
  size = 'md',
  showSeed = true,
  showRecord = true,
  className = '',
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  const sizeConfig = {
    sm: {
      height: 'h-8',
      padding: 'px-2 py-1',
      fontSize: 'text-xs',
      seedSize: 'text-xs w-5 h-5',
      logoSize: 'w-4 h-4',
    },
    md: {
      height: 'h-12',
      padding: 'px-3 py-2',
      fontSize: 'text-sm',
      seedSize: 'text-sm w-6 h-6',
      logoSize: 'w-7 h-7', // Slightly larger at md
    },
    lg: {
      height: 'h-16',
      padding: 'px-4 py-3',
      fontSize: 'text-base',
      seedSize: 'text-base w-8 h-8',
      logoSize: 'w-10 h-10', // Slightly larger at lg
    },
  };

  const config = sizeConfig[size];

  const pressTimer = useRef<number | undefined>(undefined);
  const suppressClickRef = useRef(false);

  const clearPressTimer = () => {
    if (pressTimer.current) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = undefined;
    }
  };

  const openStatsTooltip = () => {
    setShowTooltip(true);
  };

  const handlePressStart = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!team) return;
    event.preventDefault();
    clearPressTimer();
    suppressClickRef.current = false;

    pressTimer.current = window.setTimeout(() => {
      openStatsTooltip();
      suppressClickRef.current = true;
    }, 320);
  };

  const handlePressEnd = () => {
    clearPressTimer();
  };

  const toggleTooltip = () => {
    setShowTooltip((prev) => !prev);
  };

  const handleStatsClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    toggleTooltip();
  };

  const getSlotStyle = () => {
    if (!team) {
      return {
        background: 'bg-dark-400',
        border: 'border-dark-300',
        text: 'text-text-muted',
        cursor: 'cursor-default',
        glow: '',
      };
    }

    if (isEliminated) {
      return {
        background: 'bg-gray-600 opacity-50',
        border: 'border-gray-500',
        text: 'text-gray-400 line-through',
        cursor: isLocked ? 'cursor-default' : 'cursor-pointer',
        glow: '',
        filter: 'grayscale(100%)',
      };
    }

    if (isIncorrect) {
      return {
        background: 'bg-red-900/50',
        border: 'border-red-500',
        text: 'text-red-200',
        cursor: isLocked ? 'cursor-default' : 'cursor-pointer',
        glow: 'shadow-red-glow',
      };
    }

    if (isCorrect) {
      return {
        background: 'bg-green-900/50',
        border: 'border-green-500',
        text: 'text-green-200',
        cursor: 'cursor-default',
        glow: 'shadow-green-glow',
      };
    }

    if (isSelected) {
      return {
        background: 'bg-primary-600/20',
        border: 'border-primary-500',
        text: 'text-white',
        cursor: isLocked ? 'cursor-default' : 'cursor-pointer',
        glow: 'shadow-glow',
      };
    }

    return {
      background: 'bg-dark-500',
      border: 'border-dark-300',
      text: 'text-text-primary',
      cursor: isLocked ? 'cursor-default' : 'cursor-pointer',
      glow: '',
    };
  };

  const style = getSlotStyle();

  // Left border accent color: use team's primaryColor when selected
  const leftBorderStyle: React.CSSProperties = {};
  if (isSelected && team?.primaryColor) {
    leftBorderStyle.borderLeftWidth = '3px';
    leftBorderStyle.borderLeftColor = team.primaryColor;
  }

  const getSeedDisplay = () => {
    if (!showSeed || !seed) return null;

    let seedColor = 'text-text-secondary';
    if (seed === 1) seedColor = 'text-yellow-400';
    else if (seed <= 4) seedColor = 'text-blue-400';
    else if (seed >= 12) seedColor = 'text-orange-400';

    return (
      <div className={`flex-shrink-0 flex items-center justify-center 
                     ${config.seedSize} ${seedColor} font-bold
                     bg-dark-600 rounded border border-dark-400`}>
        {seed}
      </div>
    );
  };

  const tooltipContent = team && (
    <div className="bg-dark-600 border border-dark-400 rounded-lg p-3 shadow-xl z-50">
      <div className="space-y-2 min-w-48">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-white">{team.name}</h4>
          {seed && <span className="text-xs bg-primary-500 px-1.5 py-0.5 rounded">{seed} seed</span>}
        </div>
        
        <div className="text-xs space-y-1 text-text-secondary">
          <div className="flex justify-between">
            <span>Record:</span>
            <span className="text-white">{team.record?.wins ?? 0}-{team.record?.losses ?? 0}</span>
          </div>
          <div className="flex justify-between">
            <span>Conference:</span>
            <span className="text-white">{team.conference}</span>
          </div>
          {team.resume && (
            <>
              <div className="flex justify-between">
                <span>NET Ranking:</span>
                <span className="text-white">#{team.resume.netRanking}</span>
              </div>
              {team.resume.last10 && (
                <div className="flex justify-between">
                  <span>Last 10:</span>
                  <span className="text-white">{team.resume.last10.wins}-{team.resume.last10.losses}</span>
                </div>
              )}
            </>
          )}
        </div>
        
        {confidence && (
          <div className="pt-1 border-t border-dark-400">
            <div className="flex justify-between text-xs">
              <span className="text-text-secondary">Confidence:</span>
              <span className="text-primary-400 font-semibold">{confidence}/10</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // isEliminated is visual-only (greyed out) — user can still click to change pick
  const interactive = !isLocked && !disabled;

  return (
    <div className="relative">
      <button
        type="button"
        id={`team-slot-${team?.id || 'tbd'}`}
        aria-pressed={isSelected}
        aria-disabled={!interactive}
        disabled={!interactive}
        className={`
          ${config.height} ${config.padding} ${style.background} ${style.border} ${style.text} 
          ${style.glow || ''} border rounded-lg transition-all duration-150
          flex items-center space-x-2 relative overflow-hidden text-left
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a1a]
          disabled:opacity-60 disabled:cursor-not-allowed
          ${interactive && !isSelected ? 'hover:brightness-110 hover:border-primary-400' : ''}
          ${interactive ? 'cursor-pointer' : 'cursor-not-allowed'}
          ${className}
        `}
        style={{ 
          filter: (style as any).filter || 'none',
          ...leftBorderStyle,
        }}
        onClick={() => {
          if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
          }

          if (interactive && onClick) {
            onClick();
          }
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onPointerDown={handlePressStart}
        onPointerUp={handlePressEnd}
        onPointerLeave={handlePressEnd}
        onPointerCancel={handlePressEnd}
      >
        {/* Background gradient for selected state */}
        {isSelected && (
          <div className="absolute inset-0 bg-gradient-to-r from-primary-500/20 to-transparent" />
        )}

        {/* Seed number */}
        {getSeedDisplay()}

        {/* Team logo */}
        {team && (
          <div className={`${config.logoSize} flex-shrink-0 bg-dark-600 rounded border border-dark-400 flex items-center justify-center overflow-hidden`}>
            {!logoFailed && team.logo ? (
              <img
                src={team.logo}
                alt={`${team.shortName || team.name} logo`}
                className={`${config.logoSize} rounded object-contain`}
                loading="lazy"
                decoding="async"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <span className="text-[10px] font-bold text-white/30">
                {team.shortName?.slice(0, 2).toUpperCase() || '?'}
              </span>
            )}
          </div>
        )}

        {/* Team name and info */}
        <div className="flex-1 min-w-0">
          <div className={`${config.fontSize} font-medium truncate flex items-center gap-1`}>
            {team ? (team.shortName || team.name) : (seed ? `#${seed} Seed` : 'TBD')}
            {team?.playIn && (
              <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-semibold leading-none flex-shrink-0">
                PI
              </span>
            )}
          </div>
          {team && showRecord && size !== 'sm' && (
            <div className="text-xs text-white/50">
              {team.record.wins}-{team.record.losses}
            </div>
          )}
        </div>

        {/* Status indicators */}
        <div className="flex-shrink-0 flex items-center space-x-1">
          {seed === 1 && <Crown className="w-3 h-3 text-yellow-400" />}
          
          {confidence && confidence >= 8 && (
            <Star className="w-3 h-3 text-primary-400" />
          )}
          
          {seed && seed >= 10 && isSelected && (
            <TrendingUp className="w-3 h-3 text-orange-400" />
          )}

          {confidence && size !== 'sm' && (
            <span className="text-xs bg-white/[0.04] px-1.5 py-0.5 rounded border border-white/[0.06]">
              {confidence}
            </span>
          )}

          {showStatsButton && team && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleStatsClick}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleStatsClick(event as unknown as React.MouseEvent<HTMLSpanElement>);
                }
              }}
              className="w-4 h-4 rounded-full bg-white/[0.06] border border-white/[0.12] flex items-center justify-center hover:bg-white/[0.12]"
              aria-label={`Stats for ${team.name}`}
            >
              <Info className="w-2.5 h-2.5" />
            </span>
          )}
        </div>

        {/* Lock indicator */}
        {isLocked && (
          <div className="absolute top-1 right-1">
            <div className="w-2 h-2 bg-yellow-500 rounded-full" />
          </div>
        )}
      </button>

      {/* Tooltip — CSS only, no animation library */}
      {showTooltip && team && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 animate-in fade-in duration-150">
          {tooltipContent}
          <div className="absolute top-full left-1/2 -translate-x-1/2">
            <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-dark-400" />
          </div>
        </div>
      )}
    </div>
  );
};

const TeamSlot = React.memo(TeamSlotComponent);

export default TeamSlot;
